// Customer password rules. Mirrors App\Rules\StrongPassword in mishant-api and
// mishnat (KT); the server has the final word, this only shows what is missing.
// Shared accept/reject cases: tests/password-vectors.json (copied in all three repos).

export const MIN_LENGTH = 10;
export const MAX_LENGTH = 50;
export const MIN_SCORE = 3;

// zxcvbn is ~400KB gzipped, so it loads on first use instead of in the main bundle.
let zxcvbnPromise = null;
export function loadZxcvbn() {
  if (!zxcvbnPromise) {
    zxcvbnPromise = import("zxcvbn").then((m) => m.default || m);
  }
  return zxcvbnPromise;
}

const digits = (v) => String(v ?? "").replace(/\D+/g, "").replace(/^0+/, "");
const same = (a, b) =>
  !!b && String(a).toLowerCase() === String(b).trim().toLowerCase();

/**
 * @param {string} password
 * @param {{phones?: string[], username?: string, tzl?: string, tzw?: string, email?: string}} ctx
 * @param {Function|null} zxcvbn the loaded zxcvbn function, null until it is loaded
 */
export function evaluatePassword(password, ctx = {}, zxcvbn = null) {
  const pw = password ?? "";
  const phones = (ctx.phones ?? []).map(digits).filter(Boolean);
  const ids = [ctx.tzl, ctx.tzw].filter(Boolean);
  const userInputs = [
    ctx.username,
    ctx.email ? String(ctx.email).split("@")[0] : null,
    ...ids,
    ...phones,
    // Phones are stored without the leading 0 but typed with it: without this
    // form zxcvbn scores "0501234567Ab" as strong.
    ...phones.map((phone) => "0" + phone),
  ].filter(Boolean);

  const isPhone = /^[\d\-\s+()]+$/.test(pw) && phones.includes(digits(pw));
  const isPersonal =
    isPhone ||
    same(pw, ctx.username) ||
    ids.some((id) => same(pw, id)) ||
    same(pw, ctx.email);

  const score = pw && zxcvbn ? zxcvbn(pw, userInputs).score : 0;

  const checks = {
    length: pw.length >= MIN_LENGTH && pw.length <= MAX_LENGTH,
    upper: /[A-Z]/.test(pw),
    lower: /[a-z]/.test(pw),
    digit: /\d/.test(pw),
    noSpaces: !/\s/.test(pw),
    // "Not guessable": not a common/personal password. Needs zxcvbn loaded.
    strong: pw.length > 0 && !isPersonal && score >= MIN_SCORE,
  };

  return {
    checks,
    score,
    valid: Object.values(checks).every(Boolean),
  };
}

// Letters and digits that are hard to confuse when read or typed (no O/0, I/l/1).
const UPPER = "ABCDEFGHJKLMNPQRSTUVWXYZ";
const LOWER = "abcdefghijkmnpqrstuvwxyz";
const DIGITS = "23456789";
const ALL = UPPER + LOWER + DIGITS;

function randomInt(max) {
  // Rejection sampling: no modulo bias.
  const limit = Math.floor(0x100000000 / max) * max;
  const buf = new Uint32Array(1);
  do {
    crypto.getRandomValues(buf);
  } while (buf[0] >= limit);
  return buf[0] % max;
}

const pick = (set) => set[randomInt(set.length)];

/**
 * A suggestion that passes every rule above, e.g. "kT7m-Qv4Lp-x9Rn" (12 random
 * characters in groups of 4 so it is easy to read and copy).
 * Retries until evaluatePassword accepts it, so it always satisfies the rules.
 */
export function generatePassword(ctx = {}, zxcvbn = null) {
  for (let attempt = 0; attempt < 50; attempt++) {
    const chars = [pick(UPPER), pick(LOWER), pick(DIGITS)];
    while (chars.length < 12) chars.push(pick(ALL));
    // Fisher-Yates shuffle so the guaranteed characters are not always first.
    for (let i = chars.length - 1; i > 0; i--) {
      const j = randomInt(i + 1);
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    const candidate = [
      chars.slice(0, 4).join(""),
      chars.slice(4, 8).join(""),
      chars.slice(8, 12).join(""),
    ].join("-");
    if (evaluatePassword(candidate, ctx, zxcvbn).valid) {
      return candidate;
    }
  }
  throw new Error("could not generate a valid password");
}
